import { motion } from 'framer-motion';
import { Users, ShieldCheck, Wifi, Ticket, Eye, Cpu } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { AgentGraph } from '../components/agents/AgentGraph';
import { useMetricsData } from '../hooks/useMetricsData';
import { pageTransition, staggerContainer, fadeUpItem } from '../hooks/useAnimation';

interface AgentInfo {
  name: string;
  type: string;
  icon: React.ReactNode;
  capabilities: string[];
  reasoning: 'llm' | 'rules';
  handled: number;
  avgLatencyMs: number;
  successRate: number;
}

const AGENTS: AgentInfo[] = [
  { name: 'AuthAgent', type: 'auth', icon: <ShieldCheck className="w-5 h-5" />, capabilities: ['PIN verify', 'account lookup', 'fraud/risk'], reasoning: 'llm', handled: 1284, avgLatencyMs: 60, successRate: 99.4 },
  { name: 'NetworkAgent', type: 'network', icon: <Wifi className="w-5 h-5" />, capabilities: ['outage check', 'speed test', 'diagnosis'], reasoning: 'llm', handled: 642, avgLatencyMs: 230, successRate: 97.8 },
  { name: 'TicketAgent', type: 'ticket', icon: <Ticket className="w-5 h-5" />, capabilities: ['create', 'status', 'triage', 'escalate'], reasoning: 'llm', handled: 411, avgLatencyMs: 180, successRate: 98.9 },
  { name: 'SupervisorAgent', type: 'supervisor', icon: <Eye className="w-5 h-5" />, capabilities: ['quality review', 'escalation', 'summary'], reasoning: 'llm', handled: 980, avgLatencyMs: 90, successRate: 99.9 },
];

export function Agents() {
  const { system } = useMetricsData(3000);

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Users className="w-6 h-6 text-vokentra" />
            Multi-Agent System
          </h1>
          <p className="text-sm text-text-muted mt-1">
            A2A orchestration — per-agent LLM reasoning with rule-based fallback
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-info/5 border border-info/10">
          <Cpu className="w-4 h-4 text-info" />
          <span className="text-xs text-info font-medium">{system?.activeAgents?.length ?? AGENTS.length} agents active</span>
        </div>
      </div>

      {/* Orchestration graph */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Live Orchestration</h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </div>
        <GlassCard padding="md">
          <AgentGraph animated />
          <p className="text-xs text-text-muted mt-3">
            Flow: <span className="text-text-secondary">Auth → Network / Ticket → Supervisor → reply / escalate</span>
          </p>
        </GlassCard>
      </div>

      {/* Agent fleet cards */}
      <motion.div variants={staggerContainer(0.08)} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {AGENTS.map((a) => (
          <motion.div key={a.name} variants={fadeUpItem}>
            <GlassCard padding="md" className="h-full">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-vokentra/10 text-vokentra flex items-center justify-center">{a.icon}</div>
                  <div>
                    <h3 className="text-text-primary font-semibold">{a.name}</h3>
                    <p className="text-[11px] text-text-muted">type: {a.type}</p>
                  </div>
                </div>
                <StatusBadge status="active" size="sm" />
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {a.capabilities.map((c) => (
                  <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-text-secondary border border-white/[0.06]">
                    {c}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div>
                  <p className="text-[10px] text-text-muted">Handled</p>
                  <p className="text-sm font-bold text-text-primary tabular-nums">{a.handled.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-text-muted">Avg latency</p>
                  <p className="text-sm font-bold text-text-primary tabular-nums">{a.avgLatencyMs} ms</p>
                </div>
                <div>
                  <p className="text-[10px] text-text-muted">Success</p>
                  <p className="text-sm font-bold text-success tabular-nums">{a.successRate}%</p>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[11px] text-text-muted">Reasoning</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.reasoning === 'llm' ? 'bg-info/15 text-info' : 'bg-white/10 text-text-secondary'}`}>
                  {a.reasoning === 'llm' ? 'LLM (gemma3:12b)' : 'rule-based'}
                </span>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}

export default Agents;
